import React, { useCallback } from "react";
import { NavLink } from "react-router-dom";
import SchoolIcon from "@mui/icons-material/School";
import PaymentIcon from "@mui/icons-material/Payment";
import TimelineIcon from "@mui/icons-material/Timeline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PresentToAllIcon from "@mui/icons-material/PresentToAll";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SearchIcon from "@mui/icons-material/Search";
import ListIcon from "@mui/icons-material/List";
import HistoryIcon from "@mui/icons-material/History";
import BusinessIcon from "@mui/icons-material/Business";
import PostAddIcon from "@mui/icons-material/PostAdd";

import * as SVG from "../../svgs";
import { useAuthContext } from "../../../context";
import { useAdminProviderDataContext } from "../../../context";
import { UpdateVersion } from "../index";

import s from "./styles.module.css";

const SideNav = (): React.ReactElement | null => {
  interface IStyledNavLink {
    children: React.ReactNode;
    to: string;
    className?: string;
    onClick?: () => void;
    exact?: boolean;
  }

  const {
    token,
    login,
    // isExpiredCloudFlareToken
  } = useAuthContext();
  const {
    user: { allowed_features },
  } = useAdminProviderDataContext();

  const StyledNavLink = useCallback(
    ({ children, to, className = "", onClick, exact }: IStyledNavLink) => (
      <NavLink
        className={(isActive) => (isActive ? `${s.active} ${className}` : className)}
        to={to}
        onClick={() => onClick && onClick()}
        exact={exact}
      >
        {children}
      </NavLink>
    ),
    []
  );

  if (!token) return null;

  const {
    access_activity_feed_page,
    read_agents_log,
    access_customers_page,
    access_rithmic_accounts,
    access_to_discounts_tab,
    access_to_compliance_search_page,
    access_to_passers_page,
  } = allowed_features;

  return (
    <div className={s.sidebar}>
      <UpdateVersion />

      <h1 className={s.title}>Envoy</h1>

      {(access_activity_feed_page || read_agents_log) && (
        <StyledNavLink to="/" exact data-test="activity-feed">
          <SVG.SVGDashboard w={24} m={-6} />
          Activity Feed
        </StyledNavLink>
      )}

      {allowed_features.access_to_helios_admin_page && (
        <StyledNavLink to="/helios-admin" data-test="admin" className={s.centered}>
          <SupervisorAccountIcon className={s.financialsIc} />
          Helios Admin
        </StyledNavLink>
      )}

      {access_customers_page && (
        <StyledNavLink to="/customers" data-test="customers">
          <SVG.SVGCustomer w={24} m={-6} />
          Customers
        </StyledNavLink>
      )}

      {allowed_features.access_rithmic_sftp_server && (
        <StyledNavLink to="/rithmic-files" className={s.centered}>
          <FileCopyOutlinedIcon className={s.financialsIc} />
          Rithmic files
        </StyledNavLink>
      )}

      {access_rithmic_accounts && (
        <StyledNavLink to="/rithmicAccounts" className={s.centered} data-test="rithmicAccounts">
          <PresentToAllIcon className={s.financialsIc} />
          Rithmic accounts
        </StyledNavLink>
      )}

      {allowed_features.access_rithmic_historical_orders && (
        <StyledNavLink to="/rithmicHistoricalOrders" className={s.centered}>
          <HistoryIcon className={s.financialsIc} />
          Rithmic historical orders
        </StyledNavLink>
      )}

      {allowed_features.access_rithmic_account_creation_tool && (
        <StyledNavLink to="/rithmic-account-creation-tool" className={s.centered}>
          <PostAddIcon className={s.financialsIc} />
          Rithmic account creation tool
        </StyledNavLink>
      )}

      {allowed_features.access_financials_info && (
        <StyledNavLink to="/Financials" className={s.centered} data-test="financials-link">
          <PaymentIcon className={s.financialsIc} />
          Financials
        </StyledNavLink>
      )}

      {access_to_discounts_tab && (
        <StyledNavLink to="/discounts" data-test="discounts">
          <SVG.SVGDiscount w={24} m={-6} />
          Discounts
        </StyledNavLink>
      )}

      {access_to_passers_page && (
        <StyledNavLink className={s.centered} to="/passers">
          <SchoolIcon className={s.financialsIc} />
          Passers
        </StyledNavLink>
      )}

      {allowed_features.access_agents_page && (
        <StyledNavLink to="/agents" data-test="agents-link">
          <SVG.SVGAdmin w={24} m={-6} />
          Agents
        </StyledNavLink>
      )}

      {allowed_features.access_customers_page && (
        <StyledNavLink className={s.centered} to="/xlsx-customers-data">
          <ListIcon className={s.financialsIc} />
          XLSX customers data
        </StyledNavLink>
      )}

      {allowed_features.access_to_evaluations_tab && (
        <StyledNavLink to="/evaluations" className={s.centered} data-test="evaluations">
          <SVG.Checklist margin="0 9px 0 0" />
          Evaluations
        </StyledNavLink>
      )}

      {allowed_features.access_funded_traders && (
        <StyledNavLink to="/fundedTraders" className={s.centered} data-test="fundedTraders">
          <SVG.Funds margin="0 9px 0 0" />
          Funded traders
        </StyledNavLink>
      )}

      {access_to_compliance_search_page && (
        <StyledNavLink className={s.centered} to="/compliance-search">
          <SearchIcon className={s.financialsIc} />
          Compliance search
        </StyledNavLink>
      )}

      {allowed_features.access_funded_compliance && (
        <StyledNavLink
          //?active-tab=traders-db-list
          to="/fundedCompliance"
          className={s.centered}
          data-test="fundedCompliance"
        >
          <BusinessIcon className={s.financialsIc} />
          Funded compliance
        </StyledNavLink>
      )}

      {allowed_features.access_withdrawals && (
        <StyledNavLink
          to="/withdrawalRequests"
          className={s.centered}
          data-test="withdrawalRequests"
        >
          <SVG.Withdrawals w={24} margin="0 9px 0 0" />
          Withdrawals
        </StyledNavLink>
      )}

      {allowed_features.access_manage_affiliate_accounts && (
        <StyledNavLink to="/affiliates" className={s.affiliateContainerIc}>
          <SVG.Handshake />
          <div className={s.affiliateTextContainer}>Affiliates</div>
        </StyledNavLink>
      )}

      {allowed_features.access_journeys && (
        <StyledNavLink to="/journeys" className={s.centered} data-test="journeys-link">
          <TimelineIcon className={s.financialsIc} />
          Journeys
        </StyledNavLink>
      )}

      {/*{allowed_features.access_user_messages && (*/}
      {/*  <StyledNavLink to="/messages" data-test="messages-link">*/}
      {/*    <SVG.MessageIcon className={messageIcon} />*/}
      {/*    User messages*/}
      {/*  </StyledNavLink>*/}
      {/*)}*/}

      <a
        className={s.affiliateContainerIc}
        href="https://adminsetup.heliostp.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <PersonAddIcon className={s.financialsIc} />
        LiveSim Creation
      </a>

      <StyledNavLink to="/login" onClick={() => login()}>
        <SVG.SVGLogout w={24} m={-6} />
        Logout
      </StyledNavLink>

      {allowed_features.access_read_docs && (
        <a
          href="https://docs.google.com/document/d/1uZnFnFS0DFL6KK59OKmInm0DaSh3_3soTQIXcXlsC3Q/edit"
          target="_blank"
          rel="noopener noreferrer"
          data-test="docs-link"
        >
          Docs
        </a>
      )}
    </div>
  );
};

export default SideNav;
